import { For, Show, createMemo, type Component } from 'solid-js';
import type { CasinoProfile, TableProfile } from '~/domain/types';
import { deviatesFromStandard, formatCents, isBuiltInCasino, sortTables } from '~/domain/profiles';

/**
 * Table selector (SPEC.md §5.1) — what the header button opens.
 *
 * It only ever picks. Every edit is a hop into the configurator, so choosing
 * where you are playing and describing a venue never share a screen; that split
 * is why this component knows nothing about payouts, limits or validation.
 *
 * The built-in profiles lead, presented as a casino that happens to be
 * uneditable. They are the standard figures every configured table is measured
 * against (CLAUDE.md invariant 6), so there is nothing in them to change — a
 * venue of your own starts from Add casino, which opens with those same
 * standard numbers ready to correct.
 */

export interface TableSelectorProps {
  casinos: readonly CasinoProfile[];
  tablesFor: (casinoId: string) => TableProfile[];
  activeTableId: string;
  onSelect: (table: TableProfile) => void;
  onEditCasino: (casino: CasinoProfile) => void;
  onAddCasino: () => void;
}

const TableSelector: Component<TableSelectorProps> = (props) => {
  const builtIn = createMemo(() => props.casinos.find((casino) => isBuiltInCasino(casino.id)));
  /** Everything else, by name — the order casinos were added is not meaningful. */
  const own = createMemo(() =>
    props.casinos
      .filter((casino) => !isBuiltInCasino(casino.id))
      .sort((a, b) => a.name.localeCompare(b.name)),
  );

  return (
    <div class="flex max-h-[70vh] flex-col">
      <div class="min-h-0 flex-1 space-y-3 overflow-y-auto p-2">
        <Show when={builtIn()}>
          {(casino) => (
            <section>
              <div class="mb-1 flex items-baseline justify-between gap-2 px-1">
                <h3 class="text-[11px] font-medium text-ink">{casino().name}</h3>
                <span class="rounded bg-surface-3 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.1em] text-ink-faint">
                  built-in
                </span>
              </div>
              <p class="mb-1.5 px-1 text-[10px] leading-relaxed text-ink-faint">
                Standard odds, no zero rule, a $1 chip. Kept as the reference every table
                you enter is compared against, so it cannot be edited.
              </p>
              <TableRows
                tables={props.tablesFor(casino().id)}
                casino={casino()}
                activeTableId={props.activeTableId}
                onSelect={props.onSelect}
              />
            </section>
          )}
        </Show>

        <For each={own()}>
          {(casino) => (
            <section>
              <div class="mb-1 flex items-baseline justify-between gap-2 px-1">
                <h3 class="truncate text-[11px] font-medium text-ink">{casino.name}</h3>
                <button
                  type="button"
                  onClick={() => props.onEditCasino(casino)}
                  class="shrink-0 text-[10px] text-ink-faint hover:text-ink"
                >
                  Edit
                </button>
              </div>
              <TableRows
                tables={props.tablesFor(casino.id)}
                casino={casino}
                activeTableId={props.activeTableId}
                onSelect={props.onSelect}
              />
            </section>
          )}
        </For>
      </div>

      <div class="shrink-0 border-t border-line p-2">
        <button
          type="button"
          onClick={props.onAddCasino}
          class="w-full rounded border border-line bg-surface-2 px-2 py-1.5 text-[11px] text-ink-dim hover:border-line-strong hover:text-ink"
        >
          + Add casino
        </button>
      </div>
    </div>
  );
};

const TableRows: Component<{
  tables: readonly TableProfile[];
  casino: CasinoProfile;
  activeTableId: string;
  onSelect: (table: TableProfile) => void;
}> = (props) => (
  <div class="space-y-0.5">
    <For each={sortTables(props.tables)}>
      {(table) => (
        <button
          type="button"
          onClick={() => props.onSelect(table)}
          class="flex w-full items-center justify-between gap-2 rounded border px-2 py-1.5 text-left text-[11px] transition-colors"
          classList={{
            'border-placed bg-placed/15 text-ink': table.id === props.activeTableId,
            'border-line bg-surface-2 text-ink-dim hover:border-line-strong hover:text-ink':
              table.id !== props.activeTableId,
          }}
        >
          <span class="truncate">{table.name}</span>
          <span class="flex shrink-0 items-center gap-1.5 text-[10px]">
            <Show when={deviatesFromStandard(table)}>
              <span class="rounded bg-remove/20 px-1 text-remove">non-standard</span>
            </Show>
            <span class="tnum text-ink-faint">
              {table.wheelStyle === 'single-zero' ? '0' : '0/00'} ·{' '}
              {formatCents(table.baseUnit, props.casino.currency)}
            </span>
          </span>
        </button>
      )}
    </For>

    <Show when={props.tables.length === 0}>
      <p class="px-1 py-1.5 text-[10px] text-ink-faint">
        No tables yet — Edit to add one.
      </p>
    </Show>
  </div>
);

export default TableSelector;
